import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { useApolloClient } from '@apollo/client/react';
import { Avatar } from '@chakra-ui/react';
import { useClerk, useUser } from '@clerk/react';
import { GOOGLE_SCOPE_GROUPS } from '@this+that/models';
import log from 'loglevel';
import { LuLogOut, LuPlus, LuUser, LuUsers } from 'react-icons/lu';

import { IntegrationAccount } from '../../../../../../models/integrationAccount';
import { APP_PAGES } from '../../../../../../routes/constants';
import { getGoogleIntegrationUrl } from '../../../../../../services/google.service';
import { useIntegrationsStore } from '../../../../../../store';
import { clearAllUserState } from '../../../../../../utils/clearAllUserState';
import { POPOVER_Z_INDEX } from '../../hooks/useHiUserPopover';

const CustomTooltip = lazy(
  () => import('../../../../../CustomTooltip/CustomTooltip'),
);

interface UserMenuProps {
  userName: string;
  isOptionOpen: boolean;
  integrationAccounts: IntegrationAccount[];
  currentIntegrationAccount: IntegrationAccount | null;
  setCurrentIntegrationAccount: (
    integrationAccount: IntegrationAccount,
  ) => void;
  onOptionClose: () => void;
  triggerRect?: DOMRect | null;
  onNavigate?: () => void;
}

const POPOVER_WIDTH = 300;
const POPOVER_VIEWPORT_MARGIN = 8;

const stylesheet = {
  icon: 'mt-2',
  outerWrapper: `fixed ${POPOVER_Z_INDEX.POPOVER}`,
  container:
    'bg-semantic-bg-surface rounded-semantic max-h-[500px] w-full shadow-xl border border-semantic-border flex flex-col',
  scrollArea: 'flex flex-col gap-1.5 p-4 pb-0 overflow-y-auto',
  footerArea: 'flex flex-col gap-1.5 p-4 pt-3',
  headerSection: 'flex flex-col items-center gap-1 w-full',
  caret:
    'w-3 h-3 bg-semantic-bg-surface border-r border-b border-semantic-border rotate-45 mx-auto -mt-1.5',
  button:
    'w-full text-left p-2 hover:bg-semantic-bg-hover text-semantic-text-secondary text-sm flex flex-col gap-1 items-center border border-semantic-border rounded-semantic transition-colors duration-150',
  buttonDisabled:
    'w-full text-left p-2 text-semantic-text-secondary text-sm flex flex-col gap-1 items-center border border-semantic-border rounded-semantic transition-colors duration-150 opacity-60 cursor-not-allowed',
  selectedButton: 'bg-emerald-700/15 hover:bg-emerald-700/15',
  userName:
    'font-semibold text-semantic-text-primary truncate text-left text-sm max-w-[200px] overflow-hidden whitespace-nowrap',
  mail: 'truncate text-left text-xs max-w-[200px] overflow-hidden whitespace-nowrap',
  addEmailButton:
    'bg-semantic-bg-surface text-semantic-text-accent p-2 rounded-semantic border border-dashed border-semantic-border flex justify-center text-sm items-center gap-2 mb-2 hover:bg-semantic-bg-hover w-full',
  hiUser: 'text-semantic-text-primary font-bold text-2xl text-center',
  headerText: 'text-semantic-text-secondary text-sm text-center',
  divEmailButton: 'font-bold w-full',
  divider: 'w-full border-t border-semantic-border',
  accountLink:
    'w-full text-semantic-text-secondary p-2 rounded-semantic flex items-center gap-2 hover:bg-semantic-bg-hover transition-colors duration-150 text-sm',
  divPlatform: 'flex items-center gap-2 w-full',
  divText: 'flex flex-col overflow-hidden',
  avatarColors: [
    'bg-emerald-900',
    'bg-emerald-800',
    'bg-emerald-600',
    'bg-emerald-400',
    'bg-emerald-200',
  ],
};

export function UserMenu({
  isOptionOpen,
  integrationAccounts,
  userName,
  currentIntegrationAccount,
  setCurrentIntegrationAccount,
  onOptionClose,
  triggerRect,
  onNavigate,
}: Readonly<UserMenuProps>) {
  const { t } = useTranslation();
  const { user } = useUser();
  const { isDraftSaving } = useIntegrationsStore();
  const { signOut } = useClerk();
  const apolloClient = useApolloClient();

  const thereIsIntegrationAccount = integrationAccounts.length > 0;

  const handleGetGoogleIntegrationUrl = async () => {
    try {
      const url = await getGoogleIntegrationUrl(false, [
        GOOGLE_SCOPE_GROUPS.GMAIL,
      ]);
      window.location.href = url.message;
    } catch (error) {
      log.error(error);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    await clearAllUserState(apolloClient);
    window.location.href = '/';
  };

  if (!isOptionOpen) {
    return null;
  }

  const popoverLeft = triggerRect
    ? Math.min(
        triggerRect.left,
        window.innerWidth - POPOVER_WIDTH - POPOVER_VIEWPORT_MARGIN,
      )
    : 0;
  const popoverBottom = triggerRect
    ? window.innerHeight - triggerRect.top + POPOVER_VIEWPORT_MARGIN
    : 0;

  return (
    <div
      className={stylesheet.outerWrapper}
      style={{
        bottom: popoverBottom,
        left: Math.max(POPOVER_VIEWPORT_MARGIN, popoverLeft),
        width: POPOVER_WIDTH,
      }}
    >
      <div className={stylesheet.container}>
        <div className={stylesheet.scrollArea}>
          <div className={stylesheet.headerSection}>
            <Avatar
              className={stylesheet.icon}
              size="md"
              variant="emerald"
              name={user?.fullName ?? ''}
              color="white"
            />
            <p className={stylesheet.hiUser}>
              {t('common.sidebar.hiUser.title', {
                firstName: user?.firstName ?? '',
              })}
            </p>
            {thereIsIntegrationAccount && (
              <p className={stylesheet.headerText}>
                {t('common.sidebar.hiUser.subtitle')}
              </p>
            )}
          </div>

          {integrationAccounts.map((integrationAccount, index) => {
            const isCurrent =
              currentIntegrationAccount?.identifier ===
              integrationAccount.identifier;

            return (
              <Suspense
                key={`${integrationAccount.identifier}_${index}`}
                fallback={null}
              >
                <CustomTooltip
                  value={
                    isDraftSaving
                      ? t('inbox.messageDialog.cannotChangeEmail')
                      : integrationAccount.displayName
                  }
                  previewRef={{ current: null }}
                  placement="top"
                  hasArrow={false}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (isDraftSaving) {
                        return;
                      }
                      setCurrentIntegrationAccount(integrationAccount);
                      onOptionClose();
                    }}
                    className={
                      isDraftSaving
                        ? stylesheet.buttonDisabled
                        : `${stylesheet.button} ${
                            isCurrent ? stylesheet.selectedButton : ''
                          }`
                    }
                    disabled={isDraftSaving}
                    aria-disabled={isDraftSaving}
                  >
                    <div className={stylesheet.divPlatform}>
                      <Avatar
                        size="sm"
                        variant="solid"
                        name={user?.fullName ?? ''}
                        color="white"
                        className={
                          stylesheet.avatarColors?.[
                            index % stylesheet.avatarColors.length
                          ]
                        }
                      />
                      <div className={stylesheet.divText}>
                        <p className={stylesheet.userName}>{userName}</p>
                        <p className={stylesheet.mail}>
                          {integrationAccount.displayName}
                        </p>
                      </div>
                    </div>
                  </button>
                </CustomTooltip>
              </Suspense>
            );
          })}

          <div className={stylesheet.divEmailButton}>
            <button
              type="button"
              className={stylesheet.addEmailButton}
              onClick={handleGetGoogleIntegrationUrl}
            >
              <LuPlus />
              <p>{t('common.sidebar.hiUser.addEmail')}</p>
            </button>
          </div>
        </div>

        <div className={stylesheet.footerArea}>
          <hr className={stylesheet.divider} />

          <NavLink
            to={APP_PAGES.ACCOUNT}
            className={stylesheet.accountLink}
            onClick={() => {
              onOptionClose();
              onNavigate?.();
            }}
          >
            <LuUser />
            <p>{t('common.sidebar.account')}</p>
          </NavLink>

          <NavLink
            to={APP_PAGES.TEAM}
            className={stylesheet.accountLink}
            onClick={() => {
              onOptionClose();
              onNavigate?.();
            }}
          >
            <LuUsers />
            <p>{t('common.sidebar.teamSettings')}</p>
          </NavLink>

          <button
            type="button"
            className={stylesheet.accountLink}
            onClick={handleSignOut}
          >
            <LuLogOut />
            <p>{t('common.sidebar.signOut')}</p>
          </button>
        </div>
      </div>
      <div className={stylesheet.caret} />
    </div>
  );
}
